import { Button } from '@seen/ui'
import { type ReactNode, useEffect, useSyncExternalStore } from 'react'

/**
 * Subscribes to browser connectivity changes for {@link useSyncExternalStore}.
 *
 * @param onChange - Called whenever the browser goes online or offline.
 * @returns An unsubscribe function.
 */
function subscribeToOnlineStatus(onChange: () => void): () => void {
  window.addEventListener('online', onChange)
  window.addEventListener('offline', onChange)
  return () => {
    window.removeEventListener('online', onChange)
    window.removeEventListener('offline', onChange)
  }
}

/**
 * Shown when the app boot stalls — the Medplum auth/profile call that the whole
 * app is gated on is taking too long or failed. Replaces an indefinite loading
 * spinner with an explanation and a manual reload. The boot may still recover on
 * its own if the underlying call eventually resolves (see {@link createBootWatch}).
 *
 * The dominant boot failure in production is a wake-from-sleep load whose auth
 * call fired before the network was back, so this also calls `onReload`
 * automatically when the browser regains connectivity, and swaps in offline
 * copy (with the button disabled) while `navigator.onLine` is false — reloading
 * while offline would only land on a browser error page.
 *
 * Rendered before the app's providers mount, so it depends only on Tailwind /
 * `@seen/ui` primitives, not on any context.
 *
 * @param props - Component props.
 * @param props.onReload - Called when the user clicks "Reload Orbit" or when the
 *   browser regains connectivity while this fallback is shown.
 * @returns The boot-stalled fallback UI.
 */
export function BootFallback({
  onReload,
}: {
  onReload: () => void
}): ReactNode {
  const isOnline = useSyncExternalStore(
    subscribeToOnlineStatus,
    () => navigator.onLine
  )

  useEffect(() => {
    window.addEventListener('online', onReload)
    return () => window.removeEventListener('online', onReload)
  }, [onReload])

  return (
    <div className="flex size-full min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      {isOnline ? (
        <>
          <h1 className="text-lg font-medium">
            This is taking longer than usual
          </h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            Orbit is having trouble connecting. This is usually a temporary
            network hiccup — you can keep waiting, or reload to try again.
          </p>
        </>
      ) : (
        <>
          <h1 className="text-lg font-medium">You're offline</h1>
          <p className="max-w-sm text-sm text-muted-foreground">
            Orbit can't connect because this device is offline. It will reload
            automatically as soon as your connection returns.
          </p>
        </>
      )}
      <Button disabled={!isOnline} onClick={onReload}>
        Reload Orbit
      </Button>
    </div>
  )
}
